import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * Certain variants (currently: the PurrPuss Cat Hydration Booster, Chicken and Salmon) can never
 * have their cart quantity dropped below a fixed minimum — the minus button, decrease step, and
 * manual typing of a lower number are all floored there, regardless of how the line's quantity
 * was originally added or changed since. This is a fixed floor tied to the product itself, not
 * something derived from whatever quantity happened to be added.
 *
 * @extends {QuantitySelectorComponent}
 */

/** @type {Record<string, number>} Variant ID -> minimum cart quantity. */
const FIXED_MINIMUMS = {
  '56901626822996': 1, // Cat Hydration Booster / Chicken
  '57262946681172': 1, // Cat Hydration Booster / Salmon
};

/**
 * Subscription selling plans whose cart quantity is fully frozen at whatever was chosen at
 * add-to-cart time — neither the plus nor minus button (nor manual typing) can change it once
 * the line is in the cart. Only removing the line entirely is allowed.
 * @type {Set<string>}
 */
const SUBSCRIPTION_QUANTITY_LOCKED = new Set([
  '691143704916', // Delivery: every month | 10% off ("1 month supply")
  '691143737684', // Delivery: every month | 25% off ("2 months supply")
]);

/**
 * Subscription selling plans that can only be increased, never decreased, once the line is in
 * the cart — e.g. the "3-months supply" (40% off) plan.
 * @type {Set<string>}
 */
const SUBSCRIPTION_QUANTITY_INCREASE_ONLY = new Set([
  '691143672148', // Delivery: every month | 40% off ("3 months supply")
]);

export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /** @type {number | undefined} Quantity this line had when it first loaded, once captured. */
  #subscriptionFloor;

  connectedCallback() {
    super.connectedCallback();
    this.#captureSubscriptionFloor();
    this.updateButtonStates();
  }

  #captureSubscriptionFloor() {
    if (this.#subscriptionFloor !== undefined) return;

    const sellingPlanId = this.getAttribute('data-selling-plan-id');
    if (!sellingPlanId) return;

    if (SUBSCRIPTION_QUANTITY_LOCKED.has(sellingPlanId) || SUBSCRIPTION_QUANTITY_INCREASE_ONLY.has(sellingPlanId)) {
      this.#subscriptionFloor = this.getCurrentValues().value;
    }
  }

  /**
   * If this line's variant has a fixed minimum, writes it onto the input's `min` attribute,
   * which every base-class code path (buttons, typing + blur, step validation) reads from.
   */
  #applyFixedMinimum() {
    const variantId = this.getAttribute('data-variant-id');
    const minimum = variantId ? FIXED_MINIMUMS[variantId] : undefined;
    if (minimum === undefined) return;

    const { quantityInput } = this.refs;
    if (!quantityInput) return;

    quantityInput.min = minimum.toString();
    quantityInput.setAttribute('data-min', minimum.toString());
  }

  /**
   * Writes the subscription floor onto `min` (and, for fully-locked plans, onto `max` too) so
   * every base-class code path respects it, not just the buttons. Runs after the fixed-minimum
   * check so a subscription line's own floor always wins over the general product-level floor.
   */
  #applySubscriptionLock() {
    const sellingPlanId = this.getAttribute('data-selling-plan-id');
    if (!sellingPlanId || this.#subscriptionFloor === undefined) return;

    const { quantityInput } = this.refs;
    if (!quantityInput) return;

    quantityInput.min = this.#subscriptionFloor.toString();
    quantityInput.setAttribute('data-min', this.#subscriptionFloor.toString());

    if (SUBSCRIPTION_QUANTITY_LOCKED.has(sellingPlanId)) {
      quantityInput.max = this.#subscriptionFloor.toString();
    }
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Called by the parent cart-items component after every cart update, regardless of whether
   * this element was recreated or just had its attributes patched — reasserts the fixed minimum
   * and subscription lock first, then defers to the base implementation for button disabled
   * states.
   */
  updateButtonStates() {
    this.#captureSubscriptionFloor();
    this.#applyFixedMinimum();
    this.#applySubscriptionLock();
    super.updateButtonStates();
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
